import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import esCommon from './locales/es/common.json';
import enCommon from './locales/en/common.json';

export const defaultNS = 'common';

/**
 * The languages the app ships (Spanish primary). Adding one is dropping
 * `locales/<code>/common.json` in, registering it in {@link resources}, adding the
 * code here and a label in `LANGUAGE_LABELS` — no other file needs to change.
 */
export const SUPPORTED_LANGUAGES = ['es', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Each language named in itself, for the switcher. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  es: 'Español',
  en: 'English',
};

/** The localStorage key i18next caches the user's choice under. */
export const LANGUAGE_STORAGE_KEY = 'afterbooking.language';

export const resources = {
  es: { common: esCommon },
  en: { common: enCommon },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // No hardcoded `lng`: the detector takes the cached choice (localStorage)
    // first, then the browser language, and falls back to Spanish. A choice made
    // in the switcher therefore survives reloads and new tabs.
    fallbackLng: 'es',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // `es-ES` / `en-GB` from the browser resolve to the bare code we ship.
    load: 'languageOnly',
    ns: ['common'],
    defaultNS,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
  });

// Keep the document language in sync with the active language (a11y + SEO).
const syncHtmlLang = (lng: string) => {
  document.documentElement.lang = lng;
};
syncHtmlLang(i18n.resolvedLanguage ?? 'es');
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
